import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { sanitizeSearchInput, getBSTDate } from '@/lib/utils';
import { decodeRoutePointLocation } from '@/lib/routeGeometry';

// ============================================================
// Counter Service — Queries for counter staff dashboard
// ============================================================

// Loosely-typed handle for tables/RPCs not in the generated Database types
// (route_counters, passenger_check_ins, cash_reconciliation, shift_notes, …).
// Identical runtime object; relaxes only compile-time typing. (See adminService.ts.)
const db = supabase as unknown as SupabaseClient;

// Call an RPC returning a known shape without `any`, with `this` bound.
function callRpc<T>(fn: string, args?: Record<string, unknown>) {
  return (db.rpc as unknown as (
    f: string, a?: Record<string, unknown>,
  ) => Promise<{ data: T | null; error: { message?: string } | null }>)(fn, args);
}

export interface CounterProfile {
  id: string;
  full_name: string | null;
  phone: string | null;
  email: string | null;
  avatar_url: string | null;
  role: string;
  counter_id: string | null;
  counter_name: string | null;
  counter_location: string | null;
  counter_district: string | null;
  created_at: string;
  updated_at: string;
}

export interface SearchedBooking {
  id: string;
  pnr: string;
  passenger_name: string;
  passenger_phone: string;
  travel_date: string;
  total_fare: number;
  status: string;
  boarding_point: string;
  dropping_point: string;
  payment_method: string | null;
  route: string;
  departure_time: string;
  bus_name: string;
  seat_numbers: string[];
  can_print: boolean;
  can_reveal_phone: boolean;
}

export interface TodaySummary {
  /** Everything sold today, before refunds. */
  gross_collected: number;
  /** Paid back out of the drawer today. */
  refunded_today: number;
  /** Gross minus refunds — the money actually taken and still owed in. */
  total_collected: number;
  collection_count: number;
  walkin_count: number;
  refund_count: number;
  total_bookings: number;
}

export interface PerformanceStats {
  total_sales: number;
  total_revenue: number;
  avg_daily_sales: number;
  best_day_sales: number;
}

export interface SalesChartPoint {
  date: string;
  sales: number;
  revenue: number;
}

export interface Achievement {
  key: string;
  icon: string;
  title: string;
  description: string;
  unlocked: boolean;
  unlocked_at?: string;
}

// ────────────────────────────────────────────────
// 1. getCounterProfile
// ────────────────────────────────────────────────
export async function getCounterProfile(userId: string): Promise<CounterProfile | null> {
  // PERF-03: Single query with join instead of N+1 (profile + counter in one roundtrip)
  const { data, error } = await supabase
    .from('profiles')
    .select(`
      id, full_name, phone, email, avatar_url, role, counter_id,
      created_at, updated_at,
      route_counters!counter_id (name, location, district)
    `)
    .eq('id', userId)
    .single();

  if (error || !data) return null;

  const profile = (data as unknown) as {
    id: string; full_name: string | null; phone: string | null; email: string | null;
    avatar_url: string | null; role: string; counter_id: string | null;
    created_at: string; updated_at: string;
    route_counters: { name: string; location: string; district: string } | null;
  };
  const counter = profile.route_counters;

  return {
    id: profile.id,
    full_name: profile.full_name,
    phone: profile.phone,
    email: profile.email,
    avatar_url: profile.avatar_url,
    role: profile.role,
    counter_id: profile.counter_id,
    created_at: profile.created_at,
    updated_at: profile.updated_at,
    counter_name: counter?.name || null,
    counter_location: counter?.location || null,
    counter_district: counter?.district || null,
  };
}

// ────────────────────────────────────────────────
// 2. searchBooking
// ────────────────────────────────────────────────
export async function searchBooking(query: string): Promise<SearchedBooking[]> {
  if (!query || query.trim().length < 3) return [];

  const { data, error } = await callRpc<Array<{
    id: string;
    pnr: string;
    passenger_name: string;
    passenger_phone_masked: string;
    travel_date: string;
    total_fare: number;
    status: string;
    boarding_point: string;
    dropping_point: string;
    payment_method: string | null;
    route: string;
    departure_time: string;
    bus_name: string;
    seat_numbers: string[];
    can_print: boolean;
    can_reveal_phone: boolean;
  }>>('rpc_counter_lookup', {
    p_query: query.trim(),
    p_purpose: 'Counter ticket servicing',
  });

  if (error) throw new Error(error.message || 'Booking lookup failed');

  return (data || []).map((row) => ({
    id: row.id,
    pnr: row.pnr,
    passenger_name: row.passenger_name,
    passenger_phone: row.passenger_phone_masked,
    travel_date: row.travel_date,
    total_fare: Number(row.total_fare || 0),
    status: row.status,
    boarding_point: row.boarding_point,
    dropping_point: row.dropping_point,
    payment_method: row.payment_method,
    route: row.route,
    departure_time: row.departure_time || '',
    bus_name: row.bus_name || 'Unknown',
    seat_numbers: row.seat_numbers || [],
    can_print: Boolean(row.can_print),
    can_reveal_phone: Boolean(row.can_reveal_phone),
  }));
}

export async function revealBookingPhone(bookingId: string): Promise<string> {
  const { data, error } = await callRpc<string>('rpc_counter_reveal_phone', {
    p_booking_id: bookingId,
    p_purpose: 'Counter identity confirmation',
  });
  if (error) throw new Error(error.message || 'Phone reveal failed');
  return data || '';
}

// ────────────────────────────────────────────────
// 3. collectPayment
// ────────────────────────────────────────────────
export async function collectPayment(bookingId: string) {
  // AUDIT-05: Verify caller has counter_staff or admin role before RPC
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Authentication required');
  const { data: profile } = await supabase.from('profiles').select('role').eq('id', user.id).single();
  if (!profile || !['counter_staff', 'admin'].includes(profile.role)) {
    throw new Error('Unauthorized: counter staff access required');
  }

  const { data, error } = await callRpc<{ success: boolean; error?: string }>('collect_counter_payment', {
    p_booking_id: bookingId,
  });
  if (error) throw error;
  return data as { success: boolean; error?: string };
}

// ────────────────────────────────────────────────
// 4. createWalkInBooking
// ────────────────────────────────────────────────
export async function createWalkInBooking(params: {
  scheduleId: string;
  travelDate: string;
  seatIds: string[];
  boarding: string;
  dropping: string;
  fare: number;
  name: string;
  phone: string;
}) {
  // AUDIT-05: Verify caller has counter_staff or admin role before RPC
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Authentication required');
  const { data: profile } = await supabase.from('profiles').select('role').eq('id', user.id).single();
  if (!profile || !['counter_staff', 'admin'].includes(profile.role)) {
    throw new Error('Unauthorized: counter staff access required');
  }

  const { data, error } = await callRpc<{ success: boolean; booking_id?: string }>('create_counter_booking', {
    p_schedule_id: params.scheduleId,
    p_travel_date: params.travelDate,
    p_seat_ids: params.seatIds,
    p_boarding: params.boarding,
    p_dropping: params.dropping,
    p_name: params.name,
    p_phone: params.phone,
  });
  if (error) throw error;
  return data as { success: boolean; booking_id?: string };
}

// ────────────────────────────────────────────────
// 5. getTodaySummary
// ────────────────────────────────────────────────
export async function getTodaySummary(staffId: string): Promise<TodaySummary> {
  const today = getBSTDate();
  const from = `${today}T00:00:00`;
  const to = `${today}T23:59:59`;

  // Refunds are read alongside sales, not after. A counter refund is paid
  // straight out of the drawer, so a "collected" figure that ignores them
  // reports money the clerk no longer has: sell 5,000, refund 500, and the old
  // figure still read 5,000 while the drawer held 4,500. That gap is exactly
  // what a shift close then surfaced as an unexplained shortage.
  const [salesResult, refundResult] = await Promise.all([
    supabase
      .from('counter_sales')
      .select('amount, sale_type')
      .eq('staff_id', staffId)
      .gte('created_at', from)
      .lte('created_at', to),
    supabase
      .from('counter_refunds')
      .select('amount')
      .eq('staff_id', staffId)
      .gte('created_at', from)
      .lte('created_at', to),
  ]);

  const sales = (salesResult.data || []) as Array<{ amount: number; sale_type: string }>;
  const refunds = (refundResult.data || []) as Array<{ amount: number }>;

  const gross = sales.reduce((sum, s) => sum + Number(s.amount || 0), 0);
  const refunded = refunds.reduce((sum, r) => sum + Number(r.amount || 0), 0);

  return {
    gross_collected: gross,
    refunded_today: refunded,
    // What the clerk is actually answerable for at the end of the day.
    total_collected: gross - refunded,
    collection_count: sales.filter(s => s.sale_type === 'collection').length,
    walkin_count: sales.filter(s => s.sale_type === 'walk_in').length,
    refund_count: refunds.length,
    total_bookings: sales.length,
  };
}

// ────────────────────────────────────────────────
// 6. getTodayBookings — removed 2026-07-31
// ────────────────────────────────────────────────
// Its only caller was the legacy `/counter/dashboard`, deleted with the rest of
// that tree. The POS reaches bookings through `rpc_counter_lookup`, which is an
// exact, audited, phone-masked projection — counter staff deliberately have no
// broad `bookings` SELECT, and this function was the last thing relying on one.

// ────────────────────────────────────────────────
// 7. getUpcomingDepartures
// ────────────────────────────────────────────────
export async function getUpcomingDepartures(counterId: string) {
  // BUG-02: Use BST-aware day-of-week (not browser local timezone)
  const bstNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Dhaka' }));
  const dayOfWeek = bstNow.getDay();

  // Find routes this counter is on
  const { data: counterRoutes } = await supabase
    .from('route_counters')
    .select('route_id')
    .eq('id', counterId);

  if (!counterRoutes || counterRoutes.length === 0) return [];

  const routeIds = counterRoutes.map(r => r.route_id);

  const { data: schedules } = await supabase
    .from('schedules')
    .select(`
      id, departure_time,
      routes (origin, destination),
      buses (name, type, total_seats)
    `)
    .eq('status', 'active')
    .in('route_id', routeIds)
    .contains('days_of_week', [dayOfWeek])
    .order('departure_time', { ascending: true })
    .limit(8);

  if (!schedules) return [];

  // Get booking counts through a row-minimised RPC. Direct bookings SELECT is
  // intentionally not available to counter staff outside their own sales.
  const today = getBSTDate();
  const scheduleIds = schedules.map(s => s.id);
  const { data: bookingCountRows, error: countError } = await callRpc<Array<{
    schedule_id: string;
    passenger_count: number;
  }>>('rpc_counter_departure_counts', {
    p_schedule_ids: scheduleIds,
    p_travel_date: today,
  });

  const bookingCounts: Record<string, number> = {};
  if (!countError) {
    (bookingCountRows || []).forEach((row) => {
      bookingCounts[row.schedule_id] = Number(row.passenger_count || 0);
    });
  }

  return schedules.map((s) => {
    const row = s as unknown as { id: string; departure_time: string; routes?: { origin: string; destination: string }; buses?: { name: string; type: string; total_seats: number } };
    const route = row.routes;
    const bus = row.buses;
    const totalSeats = bus?.total_seats || 40;
    const passengers = bookingCounts[row.id] || 0;

    return {
      id: row.id,
      route: `${route?.origin || '?'} → ${route?.destination || '?'}`,
      time: row.departure_time?.slice(0, 5) || '',
      coach: bus?.name || 'Unknown',
      coachType: bus?.type || 'AC',
      passengers,
      totalSeats,
      occupancy: Math.round((passengers / totalSeats) * 100),
    };
  });
}

// ────────────────────────────────────────────────
// 8. getPerformanceStats
// ────────────────────────────────────────────────
export async function getPerformanceStats(staffId: string): Promise<PerformanceStats> {
  // Limit to last 90 days to prevent unbounded scan as table grows
  const lookback = new Date(Date.now() - 90 * 86400000).toISOString();

  const { data } = await supabase
    .from('counter_sales')
    .select('amount, created_at')
    .eq('staff_id', staffId)
    .gte('created_at', lookback)
    .order('created_at', { ascending: true });

  const sales = data || [];

  if (sales.length === 0) {
    return { total_sales: 0, total_revenue: 0, avg_daily_sales: 0, best_day_sales: 0 };
  }

  const total_sales = sales.length;
  const total_revenue = sales.reduce((sum, s) => sum + s.amount, 0);

  // Group by date for averages and best day
  const dailyMap: Record<string, number> = {};
  sales.forEach(s => {
    const date = s.created_at.split('T')[0];
    dailyMap[date] = (dailyMap[date] || 0) + 1;
  });

  const dailyCounts = Object.values(dailyMap);
  const avg_daily_sales = Math.round(dailyCounts.reduce((a, b) => a + b, 0) / dailyCounts.length);
  const best_day_sales = Math.max(...dailyCounts);

  return { total_sales, total_revenue, avg_daily_sales, best_day_sales };
}

// ────────────────────────────────────────────────
// 9. getSalesChart
// ────────────────────────────────────────────────
export async function getSalesChart(staffId: string, days = 7): Promise<SalesChartPoint[]> {
  const startDate = new Date(Date.now() - days * 86400000).toISOString();

  const { data } = await supabase
    .from('counter_sales')
    .select('amount, created_at')
    .eq('staff_id', staffId)
    .gte('created_at', startDate)
    .order('created_at', { ascending: true });

  // B-05 fix: Use BST (UTC+6) dates for grouping, consistent with financeService/adminService
  const dateMap: Record<string, { sales: number; revenue: number }> = {};
  for (let i = 0; i < days; i++) {
    const d = new Date(Date.now() - (days - 1 - i) * 86400000);
    const key = getBSTDate(d);
    dateMap[key] = { sales: 0, revenue: 0 };
  }

  (data || []).forEach(s => {
    const key = getBSTDate(new Date(s.created_at));
    if (dateMap[key]) {
      dateMap[key].sales += 1;
      dateMap[key].revenue += s.amount;
    }
  });

  return Object.entries(dateMap).map(([date, d]) => ({
    date,
    sales: d.sales,
    revenue: d.revenue,
  }));
}

// ────────────────────────────────────────────────
// 10. getAchievements
// ────────────────────────────────────────────────
export async function getAchievements(staffId: string): Promise<Achievement[]> {
  // PERF-01: Bound to 180 days to prevent unbounded full-table scan
  const lookback = new Date(Date.now() - 180 * 86400000).toISOString();
  const { data } = await supabase
    .from('counter_sales')
    .select('amount, created_at')
    .eq('staff_id', staffId)
    .gte('created_at', lookback)
    .order('created_at', { ascending: true });

  const sales = data || [];
  const totalSales = sales.length;
  const totalRevenue = sales.reduce((sum, s) => sum + s.amount, 0);

  // Group by date
  const dailyMap: Record<string, number> = {};
  sales.forEach(s => {
    const date = s.created_at.split('T')[0];
    dailyMap[date] = (dailyMap[date] || 0) + 1;
  });

  const dailyCounts = Object.values(dailyMap);
  const maxInOneDay = dailyCounts.length > 0 ? Math.max(...dailyCounts) : 0;

  // Check streak (consecutive days with sales)
  const sortedDates = Object.keys(dailyMap).sort();
  let maxStreak = 0;
  let currentStreak = 1;
  for (let i = 1; i < sortedDates.length; i++) {
    const prev = new Date(sortedDates[i - 1]);
    const curr = new Date(sortedDates[i]);
    const diff = (curr.getTime() - prev.getTime()) / 86400000;
    if (diff === 1) {
      currentStreak++;
      maxStreak = Math.max(maxStreak, currentStreak);
    } else {
      currentStreak = 1;
    }
  }
  if (sortedDates.length === 1) maxStreak = 1;
  if (maxStreak === 0 && sortedDates.length > 0) maxStreak = currentStreak;

  // Check 3-week rising
  const weeklyMap: Record<string, number> = {};
  sales.forEach(s => {
    const d = new Date(s.created_at);
    const weekStart = new Date(d);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    const key = weekStart.toISOString().split('T')[0];
    weeklyMap[key] = (weeklyMap[key] || 0) + 1;
  });
  const weeklyCounts = Object.entries(weeklyMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, count]) => count);
  const isRising = weeklyCounts.length >= 3 &&
    weeklyCounts[weeklyCounts.length - 1] > weeklyCounts[weeklyCounts.length - 2] &&
    weeklyCounts[weeklyCounts.length - 2] > weeklyCounts[weeklyCounts.length - 3];

  return [
    {
      key: 'first_sale',
      icon: '🏆',
      title: 'First Sale',
      description: 'Sold your first ticket',
      unlocked: totalSales >= 1,
    },
    {
      key: '100_club',
      icon: '🎯',
      title: '100 Club',
      description: '100 lifetime ticket sales',
      unlocked: totalSales >= 100,
    },
    {
      key: 'speed_seller',
      icon: '⚡',
      title: 'Speed Seller',
      description: '10+ tickets in a single day',
      unlocked: maxInOneDay >= 10,
    },
    {
      key: '7_day_streak',
      icon: '🔥',
      title: '7-Day Streak',
      description: 'Active 7 consecutive days',
      unlocked: maxStreak >= 7,
    },
    {
      key: 'century_revenue',
      icon: '💎',
      title: 'Century Revenue',
      description: '৳100,000+ lifetime revenue',
      unlocked: totalRevenue >= 100000,
    },
    {
      key: 'rising_star',
      icon: '📈',
      title: 'Rising Star',
      description: 'Sales increased 3 weeks in a row',
      unlocked: isRising,
    },
  ];
}

// ============================================================
// Public-facing counter/route data functions
// (Used by RoutesFleet.tsx, Counters.tsx)
// ============================================================

/**
 * Some counter rows carry placeholder phone values left over from data entry
 * ("01" on Azampur and Basurhat). Rendering those as a `tel:` link hands the
 * customer a number that cannot be dialled, so treat anything shorter than a
 * real BD subscriber number as absent and show the em-dash instead.
 */
function isUsablePhone(phone?: string | null): boolean {
  if (!phone) return false;
  return phone.replace(/\D/g, '').length >= 10;
}

/**
 * District names were entered by hand and drifted: "Comilla" and "Commila" both
 * exist in the data. The Counters page groups by district → division, so the
 * misspelling silently created a phantom "Commila Division" containing a single
 * counter. Normalise on read so the display is correct even before the rows are
 * cleaned up in the database.
 */
const DISTRICT_ALIASES: Record<string, string> = {
  commila: 'Comilla',
  cumilla: 'Comilla',
};

function normaliseDistrict(district?: string | null): string {
  const clean = (district || '').replace(/\s+/g, ' ').trim();
  return DISTRICT_ALIASES[clean.toLowerCase()] || clean;
}

export interface TerminalData {
  id: string;
  name: string;
  shortName: string;
  location: string;
  district: string;
  phone: string;
  isMainTerminal: boolean;
}

export interface CounterData {
  id: string;
  routeId: string;
  name: string;
  location: string;
  district: string;
  phone: string;
  type: string;
  status: string;
  sortOrder: number;
}

export interface RouteWithCounters {
  id: string;
  from: string;
  to: string;
  counters: CounterData[];
}

export interface NetworkStats {
  activeRoutes: number;
  terminals: number;
  counterPoints: number;
  districts: number;
}

/**
 * Real counts for the public trust strip.
 *
 * The homepage previously advertised "45+ Active Routes", "94% On-Time" and
 * "2M+ Passengers", none of which exist anywhere in the data — the database has
 * five active routes — and the sign-in page contradicted it with "50K+ Happy
 * Riders / 99.2% On-Time". AGENTS.md forbids fabricated metrics, so the strip
 * now shows only numbers that can be pointed at a row.
 *
 * Points are deduplicated by name+district across `terminals` and
 * `route_counters` exactly as the Counters page does, so the two surfaces
 * cannot disagree.
 */
export async function getNetworkStats(): Promise<NetworkStats> {
  const [routesRes, terminalsRes, countersRes] = await Promise.all([
    db.from('routes').select('id').eq('status', 'active'),
    db.from('terminals').select('name, district, is_main_terminal').eq('status', 'active'),
    db.from('route_counters').select('name, district').eq('status', 'Active').eq('is_visible_to_customer', true),
  ]);

  type NamedPoint = { name?: string | null; district?: string | null; is_main_terminal?: boolean };
  const key = (p: NamedPoint) =>
    `${(p.name || '').toLowerCase().trim()}|${(p.district || '').toLowerCase().trim()}`;

  const points = new Map<string, { district: string; isMainTerminal: boolean }>();
  ((terminalsRes.data as NamedPoint[]) || []).forEach(p => {
    points.set(key(p), {
      district: normaliseDistrict(p.district),
      isMainTerminal: p.is_main_terminal === true,
    });
  });
  ((countersRes.data as NamedPoint[]) || []).forEach(p => {
    if (!points.has(key(p))) {
      points.set(key(p), { district: normaliseDistrict(p.district), isMainTerminal: false });
    }
  });

  const all = Array.from(points.values());
  return {
    activeRoutes: routesRes.data?.length ?? 0,
    terminals: all.filter(p => p.isMainTerminal).length,
    counterPoints: all.filter(p => !p.isMainTerminal).length,
    districts: new Set(all.map(p => p.district).filter(Boolean)).size,
  };
}

/**
 * Public terminal network.
 *
 * This used to read `route_counters` filtered to counter_type IN
 * ('Starting Point','Last Stop'). Only the seeded Chattogram → Cox's Bazar route
 * ever used those two enum values — every real route uses 'Origin Terminal' /
 * 'Destination Terminal'. The result was that the public Counters page listed
 * exactly two terminals, and Star Line's three actual main terminals (Feni,
 * Abdullahpur, Maniknagar) were missing entirely; searching "Feni" returned
 * "No terminals found".
 *
 * `terminals` is the real table for this — it carries `is_main_terminal`, the
 * short names and the map links, and it is what the admin panel maintains.
 */
export async function getTerminals(): Promise<TerminalData[]> {
  const { data, error } = await db
    .from('terminals')
    .select('id, name, short_name, location, district, phone, is_main_terminal, status')
    .eq('status', 'active')
    .order('is_main_terminal', { ascending: false })
    .order('name');

  if (error || !data) return [];

  type TerminalRow = {
    id: string; name: string; short_name?: string; location?: string;
    district?: string; phone?: string; is_main_terminal?: boolean;
  };

  return (data as TerminalRow[]).map((c) => {
    // Several rows carry stray leading/trailing/double spaces from the original
    // data entry (" Madhya Badda Counter", "Chauddagram Counter ").
    const name = c.name.replace(/\s+/g, ' ').trim();
    const shortName = (c.short_name || '').replace(/\s+/g, ' ').trim();
    return {
      id: c.id,
      name,
      shortName: shortName || name.replace(/\s*(Counter|Terminal|Point)\s*/gi, '').trim() || name,
      location: decodeRoutePointLocation(c.location).address,
      district: normaliseDistrict(c.district),
      phone: isUsablePhone(c.phone) ? (c.phone as string) : '—',
      isMainTerminal: c.is_main_terminal === true,
    };
  });
}

export async function getRoutesWithCounters(): Promise<RouteWithCounters[]> {
  const { data: routes, error } = await supabase
    .from('routes')
    .select('id, origin, destination, status')
    .eq('status', 'active')
    .order('origin');

  if (error || !routes) return [];

  const routeIds = routes.map(r => r.id);

  const { data: counters } = await supabase
    .from('route_counters')
    .select('id, route_id, name, location, district, phone, counter_type, status, sort_order')
    .in('route_id', routeIds)
    .order('sort_order', { ascending: true });

  type CounterRow = {
    id: string; route_id: string; name: string; location?: string; district?: string;
    phone?: string; counter_type?: string; status?: string; sort_order?: number;
  };
  const counterMap: Record<string, CounterData[]> = {};
  ((counters as CounterRow[]) || []).forEach((c) => {
    if (!counterMap[c.route_id]) counterMap[c.route_id] = [];
    const location = decodeRoutePointLocation(c.location).address;
    counterMap[c.route_id].push({
      id: c.id,
      routeId: c.route_id,
      name: c.name.replace(/\s+/g, ' ').trim(),
      location,
      district: normaliseDistrict(c.district),
      phone: isUsablePhone(c.phone) ? (c.phone as string) : '—',
      type: c.counter_type || 'Counter',
      status: c.status || 'Active',
      sortOrder: c.sort_order || 0,
    });
  });

  return routes.map((r) => ({
    id: r.id,
    from: r.origin,
    to: r.destination,
    counters: counterMap[r.id] || [],
  }));
}

// ============================================================
// Phase 23: Passenger Check-In
// ============================================================

export async function checkInPassenger(
  bookingId: string,
  staffId: string,
  boardingPoint: string,
  notes?: string
): Promise<{ success: boolean; error?: string }> {
  const { error } = await db.from('passenger_check_ins').insert({
    booking_id: bookingId,
    checked_in_by: staffId,
    boarding_point: boardingPoint,
    notes: notes || null,
  });

  return { success: !error, error: error?.message };
}

export async function getCheckInStatus(bookingIds: string[]): Promise<Record<string, boolean>> {
  if (bookingIds.length === 0) return {};

  const { data } = await db.from('passenger_check_ins')
    .select('booking_id')
    .in('booking_id', bookingIds);

  const statusMap: Record<string, boolean> = {};
  bookingIds.forEach(id => { statusMap[id] = false; });
  ((data as { booking_id: string }[]) || []).forEach((row) => { statusMap[row.booking_id] = true; });

  return statusMap;
}

// ============================================================
// Phase 23: Cash Reconciliation
// ============================================================

// A reconciliation is filed by `rpc_counter_close_shift`, which derives the
// expected amount from the shift's own journal. The client-side
// `submitCashReconciliation` that used to live here let a clerk choose both the
// expected amount and the status; it had no caller and was removed 2026-09-11.

export async function getCashReconciliations(filters?: {
  staffId?: string;
  status?: string;
  page?: number;
  limit?: number;
}): Promise<{ records: import('@/types/database').CashReconciliationEntry[]; total: number }> {
  const { staffId, status, page = 1, limit = 15 } = filters || {};
  const from = (page - 1) * limit;
  const to = from + limit - 1;

  let query = db.from('cash_reconciliation')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(from, to);

  if (staffId) query = query.eq('staff_id', staffId);
  if (status && status !== 'all') query = query.eq('status', status);

  const { data, count } = await query;

  return {
    records: (data || []) as unknown as import('@/types/database').CashReconciliationEntry[],
    total: count || 0,
  };
}

// ============================================================
// Phase 23: Shift Handover Notes
// ============================================================

export async function createShiftNote(
  counterId: string,
  staffId: string,
  shiftDate: string,
  note: string
): Promise<{ success: boolean }> {
  const { error } = await db.from('shift_notes').insert({
    counter_id: counterId,
    staff_id: staffId,
    shift_date: shiftDate,
    note,
  });

  return { success: !error };
}

export async function getShiftNotes(
  counterId: string,
  limit = 5
): Promise<import('@/types/database').ShiftNote[]> {
  const { data } = await db.from('shift_notes')
    .select('*')
    .eq('counter_id', counterId)
    .order('created_at', { ascending: false })
    .limit(limit);

  return (data || []) as unknown as import('@/types/database').ShiftNote[];
}
